import { escapeRegExp } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { filterMaybeArray } from "@examspark/common";
import {
  Button,
  Modal,
  ModalProps,
  TextInput,
  useSelectedObjects,
  VFlex,
} from "@examspark/common-ui";
import { BucketAggregation, Objective } from "@examspark/types";

import { gqlQuery } from "../../../../../api/util";
import { NewActivityContext } from "../../../../../classroom";
import { useAppContext } from "../../../../../providers/AppContext";
import { useExamBuilderSearch } from "../../hooks/useSearch";
import { StyledTableHeader } from "../ExamBuilder";
import SectionSelectInput from "../SectionSelectInput";

import ObjectiveSelectListItem from "./ObjectiveSelectListItem";

const FilterBar = styled.div`
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  border-bottom: solid 1px #ddd;
`;

const ObjectiveSelectModal = (
  props: ModalProps & {
    objectiveAuthorityId: string;
    onSubmit?: (objectives: Objective[]) => void;
  },
) => {
  const [context] = useAppContext();
  const [state] = useContext(NewActivityContext);
  const search = useExamBuilderSearch();
  const [section, setSection] = useState<string | null>(null);
  const [objectives, setObjectives] = useState<Objective[]>();
  const selected = useSelectedObjects<Objective>([]);
  const [facets, setFacets] = useState<BucketAggregation[]>();
  const [filter, setFilter] = useState("");

  const authorityId = props.objectiveAuthorityId;

  const fetchObjectives = async () => {
    if (!section) return;
    if (!state.language) return;
    if (!context.app?.workspaceId) return;

    const result = await gqlQuery<any>({
      query: /* GraphQL */ `
        query ListObjectivesByScopeSequence(
          $scope: String
          $sequence: ModelIntKeyConditionInput
          $sortDirection: ModelSortDirection
          $filter: ModelObjectiveFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listObjectivesByScopeSequence(
            scope: $scope
            sequence: $sequence
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              scope
              abbreviation
              outlineNumber
              content
              sequence
            }
          }
        }
      `,
      variables: {
        limit: 200,
        scope: section.replace("section#", ""),
      },
    });

    const _objectives = result.data?.listObjectivesByScopeSequence?.items || [];
    setObjectives(_objectives);

    /** get resource counts for each objective */
    const results = await search.searchItems({
      exclude: ["passages", "objectives", "interactionTypes"],
      filter: {
        scope: section.replace("section#", ""),
      },
    });
    setFacets(
      filterMaybeArray(results?.aggregations?.objectiveId?.buckets ?? []),
    );
  };

  useEffect(() => {
    fetchObjectives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  useEffect(() => {
    if (!section && state.sections?.length) {
      setSection(state.sections[0]);
    }
  }, [section, state.sections]);

  useEffect(() => {
    if (props.isOpen) {
      setFacets(undefined);
      // updateSelected.clear();
      fetchObjectives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const sortedSections = [...(state.sections ?? [])].sort();

  return (
    <Modal
      size="medium"
      title="Select Objectives"
      flexBodyColumn
      buttons={
        <>
          <Button
            title="Close"
            data-cy="modal-button-close"
            variant="contained"
            onClick={props.onRequestClose}
          >
            Close
          </Button>
          <Button
            title="Select Objectives"
            data-cy="modal-button-submit"
            autoFocus
            variant="contained"
            color="primary"
            onClick={() => {
              props.onSubmit?.(selected.values);
            }}
            // disabled={!selected.length}
          >
            Select {selected.values.length}{" "}
            {selected.values.length === 1 ? "Objective" : "Objectives"}
          </Button>
        </>
      }
      {...props}
    >
      <VFlex>
        <FilterBar>
          <SectionSelectInput
            authorityId={authorityId}
            documentId={state.document ?? ""}
            placeholder="Select Grade Level..."
            value={section}
            objectiveSections={state.sections}
            onChange={(value) => setSection(value)}
            options={sortedSections.map((section) => ({
              value: section,
              label: section,
            }))}
          />

          <TextInput
            placeholder="Filter Standards"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </FilterBar>
        <div>
          <StyledTableHeader.Table>
            <StyledTableHeader.Column flex={1} textAlign="left">
              Standard
            </StyledTableHeader.Column>
            <StyledTableHeader.Column textAlign="right">
              Item Qty Available
            </StyledTableHeader.Column>
          </StyledTableHeader.Table>
        </div>
        <VFlex.Main>
          <div>
            {objectives?.map((o) => {
              if (!o.abbreviation) return null;
              const facet = facets?.find((f) => f.key === o.id);
              const count = facet?.doc_count || 0;
              const fuzzyAbbreviation = o.abbreviation.replace(
                /[^a-zA-Z0-9]/g,
                "",
              );
              if (!o.content) return null;
              if (!count) return null;
              if (
                !!filter.length &&
                !o.content.match(new RegExp(escapeRegExp(filter), "i")) &&
                !o.abbreviation.match(new RegExp(escapeRegExp(filter), "i")) &&
                !fuzzyAbbreviation.match(new RegExp(escapeRegExp(filter), "i"))
              ) {
                return null;
              }

              return (
                <ObjectiveSelectListItem
                  key={o.id}
                  objective={o}
                  count={count}
                  highlightText={filter}
                  onClick={() => {
                    if (state.objectives.find((obj) => obj.id === o.id)) return;
                    // storing "count" in versionCurrent because we need a typesafe field to borrow
                    selected.toggle({ ...o, versionCurrent: count });
                  }}
                  checked={
                    selected.contains(o) ||
                    !!state.objectives.find((obj) => obj.id === o.id)
                  }
                  disabled={!!state.objectives.find((obj) => obj.id === o.id)}
                />
              );
            })}
          </div>
        </VFlex.Main>
      </VFlex>
    </Modal>
  );
};

export default ObjectiveSelectModal;
